import type { Card, Category } from '@klopotun/contracts';
import { useState } from 'react';
import { Controller, useWatch, type Control } from 'react-hook-form';
import { useCardsQuery } from '../../../entities/card/index.js';
import { useCategoriesQuery } from '../../../entities/category/index.js';
import { useMeQuery } from '../../../entities/me/index.js';
import { DateField, SelectField, TextField, type SelectOption } from '../../../shared/ui/index.js';
import type { PaymentFormValues } from '../model/payment-form-values.js';
import { NewCardPanel } from './new-card-panel/new-card-panel.js';
import { NewCategoryPanel } from './new-category-panel/new-category-panel.js';

const LIST_PENDING_HINT = 'Завантажуємо перелік…';
const LIST_FAILED_HINT = 'Перелік не завантажився — спробуйте пізніше';

/** Never a record id: picking it opens the panel instead of changing the field. */
const NEW_CARD_OPTION: SelectOption = { value: '__new-card__', label: '+ Нова картка' };

const NEW_CATEGORY_OPTION: SelectOption = {
  value: '__new-category__',
  label: '+ Нова категорія',
};

type ListState = {
  isPending: boolean;
  isError: boolean;
};

const hintOf = (state: ListState): string | undefined => {
  if (state.isPending) {
    return LIST_PENDING_HINT;
  }

  if (state.isError) {
    return LIST_FAILED_HINT;
  }

  return undefined;
};

/**
 * An archived card stays in the picker while this very payment points at it. Dropping it would let
 * a save quietly detach the payment from the card its history is filed under, because PATCH always
 * carries the whole body.
 */
const isOfferable = (record: { id: string; isArchived: boolean }, selectedId: string): boolean =>
  !record.isArchived || record.id === selectedId;

const cardOptionsOf = (cards: Card[], selectedId: string): SelectOption[] =>
  cards
    .filter((card) => isOfferable(card, selectedId))
    .map((card) => ({
      value: card.id,
      label: card.isArchived ? `${card.label} (в архіві)` : card.label,
    }));

const categoryOptionsOf = (categories: Category[], selectedId: string): SelectOption[] =>
  categories
    .filter((category) => isOfferable(category, selectedId))
    .map((category) => ({
      value: category.id,
      label: category.emoji === null ? category.name : `${category.emoji} ${category.name}`,
    }));

type DetailsStepProps = {
  control: Control<PaymentFormValues>;
};

export const DetailsStep = ({ control }: DetailsStepProps) => {
  const [isAddingCard, setIsAddingCard] = useState(false);
  const [isAddingCategory, setIsAddingCategory] = useState(false);
  const me = useMeQuery();
  const cards = useCardsQuery({ includeArchived: true });
  const categories = useCategoriesQuery({ includeArchived: true });
  const selectedCardId = useWatch({ control, name: 'cardId' });
  const selectedCategoryId = useWatch({ control, name: 'categoryId' });

  return (
    <>
      <Controller
        control={control}
        name="cardId"
        render={({ field, fieldState }) => (
          <>
            <SelectField
              label="Картка"
              name={field.name}
              options={[...cardOptionsOf(cards.data ?? [], selectedCardId), NEW_CARD_OPTION]}
              value={field.value}
              onChange={(event) => {
                if (event.target.value === NEW_CARD_OPTION.value) {
                  setIsAddingCard(true);
                  return;
                }

                field.onChange(event);
              }}
              onBlur={field.onBlur}
              ref={field.ref}
              placeholder="Без картки"
              disabled={cards.isPending}
              hint={hintOf(cards)}
              error={fieldState.error?.message}
            />
            {isAddingCard && (
              <NewCardPanel
                baseCurrency={me.data?.settings.baseCurrency ?? null}
                onCreated={(card) => {
                  field.onChange(card.id);
                  setIsAddingCard(false);
                }}
                onCancel={() => {
                  setIsAddingCard(false);
                }}
              />
            )}
          </>
        )}
      />

      <Controller
        control={control}
        name="categoryId"
        render={({ field, fieldState }) => (
          <>
            <SelectField
              label="Категорія"
              name={field.name}
              options={[
                ...categoryOptionsOf(categories.data ?? [], selectedCategoryId),
                NEW_CATEGORY_OPTION,
              ]}
              value={field.value}
              onChange={(event) => {
                if (event.target.value === NEW_CATEGORY_OPTION.value) {
                  setIsAddingCategory(true);
                  return;
                }

                field.onChange(event);
              }}
              onBlur={field.onBlur}
              ref={field.ref}
              placeholder="Без категорії"
              disabled={categories.isPending}
              hint={hintOf(categories)}
              error={fieldState.error?.message}
            />
            {isAddingCategory && (
              <NewCategoryPanel
                categories={categories.data ?? []}
                onCreated={(category) => {
                  field.onChange(category.id);
                  setIsAddingCategory(false);
                }}
                onCancel={() => {
                  setIsAddingCategory(false);
                }}
              />
            )}
          </>
        )}
      />

      <Controller
        control={control}
        name="endDate"
        render={({ field, fieldState }) => (
          <DateField
            label="Кінцева дата"
            name={field.name}
            value={field.value}
            onChange={field.onChange}
            onBlur={field.onBlur}
            ref={field.ref}
            placeholder="Без кінцевої дати"
            clearable
            error={fieldState.error?.message}
          />
        )}
      />

      <Controller
        control={control}
        name="cancelUrl"
        render={({ field, fieldState }) => (
          <TextField
            label="Як скасувати"
            name={field.name}
            value={field.value}
            onChange={field.onChange}
            onBlur={field.onBlur}
            ref={field.ref}
            type="url"
            inputMode="url"
            placeholder="https://netflix.com/account"
            hint="Посилання на сторінку, де підписку вимикають"
            error={fieldState.error?.message}
          />
        )}
      />

      <Controller
        control={control}
        name="note"
        render={({ field, fieldState }) => (
          <TextField
            label="Нотатка"
            name={field.name}
            value={field.value}
            onChange={field.onChange}
            onBlur={field.onBlur}
            ref={field.ref}
            placeholder="Тариф на двох"
            error={fieldState.error?.message}
          />
        )}
      />
    </>
  );
};
